import React, { act, useEffect, useRef, useState } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import { GroupProps, ThreeEvent, useThree } from "@react-three/fiber";
import * as THREE from "three";

interface ModelProps extends GroupProps {
  modelPath: string;
  hideState?: boolean;
  isPlaying: boolean;
  animate: boolean;
  selectedStepDetails: any;
  setIsPlaying: (value: boolean) => void;
  handleStepChange: (value: string) => void;
}

interface GLTFResult {
  nodes: { [key: string]: THREE.Mesh };
  materials: { [key: string]: THREE.Material };
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
}

export function DynamicModel({
  modelPath,
  hideState,
  isPlaying,
  setIsPlaying,
  animate,
  selectedStepDetails,
  ...props
}: ModelProps) {
  const { scene, animations } = useGLTF(modelPath) as unknown as GLTFResult;
  const groupRef = useRef<THREE.Group>(null);
  const { mixer } = useAnimations(animations, groupRef);
  const { set, camera } = useThree();

  const [time, setTime] = useState<number>(selectedStepDetails.startTime);
  const [pausedTime, setPausedTime] = useState<number | null>(null);
  const [customCamera, setCustomCamera] =
    useState<THREE.PerspectiveCamera | null>(null);

  useEffect(() => {
    const action = mixer.clipAction(animations[0]);
    if (isPlaying) {
      action.paused = false;
      action.play();
      action.time =
        pausedTime !== null ? pausedTime : selectedStepDetails.startTime;
      action.loop = THREE.LoopRepeat;
      setPausedTime(null);
    } else {
      if(action.time<selectedStepDetails.endTime){
      setPausedTime(action.time);
      }
      else{
        setTime(selectedStepDetails.startTime)
        setPausedTime(null)
      }
      action.paused = true;
    }
  }, [isPlaying]);

  useEffect(() => {
    // Find the camera in the imported scene
    let foundCamera: THREE.PerspectiveCamera | null = null;
    scene.traverse((child) => {
      if (child.type === "PerspectiveCamera") {
        foundCamera = child as THREE.PerspectiveCamera;
      }
    });
    if (foundCamera) {
      setCustomCamera(foundCamera);
      set({ camera: foundCamera }); // Set this as the scene's active camera
    }
    setPausedTime(null);
    const action = mixer.clipAction(animations[0]);
    setTime(selectedStepDetails.startTime)
    if (isPlaying) {
      action.paused = false;
      action.play();
      action.time = selectedStepDetails.startTime;
      action.loop = THREE.LoopRepeat;
    }
  }, [selectedStepDetails]);

  useEffect(() => {
    let intervalId: any;
    if (time < selectedStepDetails.endTime && isPlaying) {
      intervalId = setInterval(() => setTime(time + 1), 1000);
    } else {
      setIsPlaying(false)
    }
    return () => clearInterval(intervalId);
  }, [time,isPlaying]);

  // Function to handle clicking on any model part
  const handlePointerDown = (event: any) => {
    event.stopPropagation(); // Prevent event bubbling
    const clickedObject = event.object;
    props.handleStepChange(clickedObject.name.split("_")[0]);
  };

  return (
    <>
      <directionalLight position={[0, 0, 5]} intensity={0.6} />
      {customCamera && <primitive object={customCamera} />}
      <primitive
        ref={groupRef}
        {...props}
        object={scene}
        onPointerDown={handlePointerDown} // Attach event listener to model
      />
    </>
  );
}

export function ModelLoader({ modelPath, ...props }: ModelProps) {
  const [isPlaying, setIsPlaying] = useState(props.isPlaying);

  useEffect(() => {
    setIsPlaying(props.isPlaying);
  }, [props.isPlaying]);

  return (
    <DynamicModel modelPath={modelPath} {...props} isPlaying={isPlaying} />
  );
}
